import { EnumHelper } from '@/helpers/enumHelper';
import { FormHelper } from '@/helpers/formHelper';
import { AlertService } from '@/services/alert.service';
import { EmailWithTLDValidator } from '@/validators/EmailWithTLDValidator';
import { SiretValidator } from '@/validators/SiretValidator';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CompaniesService, Company, Location, ServiceType } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { Address, AddressLocation, AddressFormComponent } from '../ui-forms/address-form/address.component';
import { SelectComponent } from '../ui-forms/select/select.component';

import { NgTemplateOutlet } from '@angular/common';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { InputComponent } from '../ui-forms/input/input.component';

export class CompanyDialogData {
  company: Company;
  edition = false;
}

@Component({
    selector: 'app-company-dialog',
    templateUrl: './company-dialog.component.html',
    styleUrls: ['./company-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, MatDialogModule, FormsModule, ReactiveFormsModule, SelectComponent, AddressFormComponent, InputComponent]
})
export class CompanyDialogComponent implements OnInit, OnDestroy {
  sub = new Subscription();
  dialogTitle: string;
  isUpdate: boolean;
  isLoading = false;

  serviceList: ServiceType[] = EnumHelper.getApiEnumList(ServiceType);

  get form_name() {
    return this.form.get('name') as FormControl<string>;
  }
  get form_summary() {
    return this.form.get('summary') as FormControl<string>;
  }
  get form_email() {
    return this.form.get('email') as FormControl<string>;
  }
  get form_services() {
    return this.form.get('services') as FormControl<ServiceType[]>;
  }
  get form_description() {
    return this.form.get('description') as FormControl<string>;
  }
  get form_phoneNumber() {
    return this.form.get('phoneNumber') as FormControl<string>;
  }
  get form_website() {
    return this.form.get('website') as FormControl<string>;
  }
  get form_siret() {
    return this.form.get('siret') as FormControl<string>;
  }
  get form_vatNumber() {
    return this.form.get('vatNumber') as FormControl<string>;
  }

  get form_address() {
    return this.form.get('address') as FormControl<Address | null>;
  }
  get form_street() {
    return this.form_address ? this.form_address.value.street : null;
  }

  form = this.fb.group({
    name: [null, Validators.required],
    summary: [null, [Validators.required, Validators.maxLength(50)]],
    email: [null, [Validators.required, EmailWithTLDValidator]],
    services: [[], Validators.required],
    description: [null],
    phoneNumber: [null],
    website: [null],
    siret: [null, SiretValidator],
    vatNumber: [null],
    address: new FormControl<Address>(null)
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CompanyDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CompanyDialogComponent>,
    private companiesService: CompaniesService,
    private alertService: AlertService
  ) {
    this.isUpdate = this.data.edition;
    this.dialogTitle = this.data.edition ? 'companyDialog.titleUpdate' : 'companyDialog.titleCreate';
  }

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  initForm() {
    if (this.isUpdate) {
      this.form_name.setValue(this.data.company.name || null);
      this.form_email.setValue(this.data.company.email || null);
      this.form_summary.setValue(this.data.company.summary || null);
      this.form_services.setValue(this.data.company.services || []);
      this.form_description.setValue(this.data.company.description || null);
      this.form_phoneNumber.setValue(this.data.company.phoneNumber || null);
      this.form_website.setValue(this.data.company.webSite || null);
      this.form_siret.setValue(this.data.company.siret || null);
      this.form_vatNumber.setValue(this.data.company.vatNumber || null);

      if (this.data.company.address) {
        const location: AddressLocation = {
          latitude: this.data.company?.location?.latitude || null,
          longitude: this.data.company?.location?.longitude || null,
          altitude: this.data.company?.location?.altitude || null
        };
        this.form_address.setValue({
          street: this.data.company.address.street || null,
          city: this.data.company.address.city || null,
          complement: this.data.company.address.complement || null,
          country: this.data.company.address.country || null,
          postalCode: this.data.company.address.postalCode || null,
          location
        });
      } else {
        this.form_address.setValue(null);
      }
    }
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      if (this.isUpdate) {
        this.update();
      } else {
        this.create();
      }
    }
  }

  create() {
    this.isLoading = true;
    const body = this.getCreateOrUpdateBody();
    this.sub = this.companiesService.createCompany(body).subscribe({
      next: (uid: string) => {
        this.alertService.success('Partenaire créé');
        this.dialogRef.close(true);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  update() {
    this.isLoading = true;
    const body = this.getCreateOrUpdateBody();
    this.sub = this.companiesService.updateCompany(this.data.company.uid, body).subscribe({
      next: () => {
        this.alertService.success('Partenaire modifié');
        this.dialogRef.close(true);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  getCreateOrUpdateBody(): Company {
    FormHelper.setFcNullIfAllEmpty(this.form_address);
    const location: Location = this.form_address.value?.location || null;
    const company = { ...this.form.value };
    if (company?.address?.location) {
      delete company.address.location;
    }
    if (location) {
      company.location = { ...location };
    }
    return company;
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
